import { reactive, ref, computed, type ComputedRef } from "vue";

export const DATE_PRESETS = [
  { label: "QTD", value: "qtd" },
  { label: "YTD", value: "ytd" },
  { label: "Custom", value: "custom" },
] as const;

export const OPPORTUNITY_TYPE_OPTIONS = [
  "New Business",
  "Existing Business",
  "Upsell",
  "Renewal",
  "Cross-Sell",
] as const;

export interface AdvancedFilters {
  datePreset: string;
  startDate: Date;
  endDate: Date;
  compareStartDate?: Date;
  compareEndDate?: Date;
  salesRep: string;
  valueMin: string;
  valueMax: string;
  leadSources: string[];
  stages: string[];
  opportunityTypes: string[];
}

export interface OpportunityDashboardRequestBody {
  organizationId: string;
  status?: string;
  period?: string;
  startDate?: string;
  endDate?: string;
  compareTo?: string;
  compareStartDate?: string;
  compareEndDate?: string;
  leadSourceIds?: string[];
  opportunityStageIds?: string[];
  dealTypeIds?: string[];
  ownerIds?: string[];
  minValue?: number;
  maxValue?: number;
}

function getDefaultAdvancedFilters(): AdvancedFilters {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 30);
  return {
    datePreset: "custom",
    startDate: start,
    endDate: now,
    salesRep: "all",
    valueMin: "",
    valueMax: "",
    leadSources: [],
    stages: [],
    opportunityTypes: [],
  };
}

export function mapPeriodToBackend(period: string): string | null {
  const periodMap: Record<string, string> = {
    today: "today",
    yesterday: "yesterday",
    week: "thisweek",
    pastweek: "pastweek",
    month: "thismonth",
    pastmonth: "pastmonth",
    qtd: "qtd",
    ytd: "ytd",
  };
  return periodMap[period] || null;
}

export function mapCompareToToBackend(compareTo: string): string {
  const compareMap: Record<string, string> = {
    none: "none",
    yesterday: "yesterday",
    last_week: "sameDaysLastWeek",
    last_month: "sameDaysLastMonth",
    last_quarter: "sameDaysLastQuarter",
    last_year: "sameDaysLastYear",
    custom: "custom",
  };
  return compareMap[compareTo] || "none";
}

export function formatDateForAPI(date: Date | string | undefined): string | undefined {
  if (!date) return undefined;
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return undefined;
  return value.toISOString();
}

export function useOpportunityDashboardFilters(refs: {
  leadSourceMap: ComputedRef<Map<string, string>>;
  stageMap: ComputedRef<Map<string, string>>;
  userMap: ComputedRef<Map<string, string>>;
}) {
  const selectedStatus = ref("all");
  const selectedPeriod = ref("custom");
  const selectedCompareTo = ref("last_week");
  const advancedFiltersOpen = ref(false);
  const advancedFilters = reactive<AdvancedFilters>(getDefaultAdvancedFilters());

  const showPrimaryCustomPicker = ref(false);
  const showCompareCustomPicker = ref(false);
  const primaryCustomRange = reactive({ from: "" as string, to: "" as string });
  const compareCustomRange = reactive({ from: "" as string, to: "" as string });

  const advancedFiltersCount = computed(() => {
    let count = 0;
    if (advancedFilters.datePreset !== "custom") count += 1;
    if (advancedFilters.salesRep !== "all") count += 1;
    if (advancedFilters.valueMin || advancedFilters.valueMax) count += 1;
    if (advancedFilters.leadSources.length) count += 1;
    if (advancedFilters.stages.length) count += 1;
    if (advancedFilters.opportunityTypes.length) count += 1;
    return count;
  });

  function buildRequestBody(
    organizationId: string,
    overrides?: Record<string, any>
  ): OpportunityDashboardRequestBody {
    const requestBody: OpportunityDashboardRequestBody = {
      organizationId,
      ...overrides,
    };

    // Map status
    if (selectedStatus.value && selectedStatus.value !== "all") {
      requestBody.status = selectedStatus.value;
    }

    // Map period
    if (selectedPeriod.value && selectedPeriod.value !== "custom") {
      const backendPeriod = mapPeriodToBackend(selectedPeriod.value);
      if (backendPeriod) {
        requestBody.period = backendPeriod;
      }
    } else if (advancedFilters.startDate && advancedFilters.endDate) {
      requestBody.startDate = formatDateForAPI(advancedFilters.startDate);
      requestBody.endDate = formatDateForAPI(advancedFilters.endDate);
    }

    // Map compareTo
    if (selectedCompareTo.value && selectedCompareTo.value !== "none") {
      const backendCompareTo = mapCompareToToBackend(selectedCompareTo.value);
      requestBody.compareTo = backendCompareTo;

      if (
        backendCompareTo === "custom" &&
        advancedFilters.compareStartDate &&
        advancedFilters.compareEndDate
      ) {
        requestBody.compareStartDate = formatDateForAPI(advancedFilters.compareStartDate);
        requestBody.compareEndDate = formatDateForAPI(advancedFilters.compareEndDate);
      }
    }

    // Map advanced filters
    if (advancedFilters.leadSources && advancedFilters.leadSources.length > 0) {
      const leadSourceIds = advancedFilters.leadSources
        .map((name) => refs.leadSourceMap.value.get(name))
        .filter((id): id is string => id !== undefined);
      if (leadSourceIds.length > 0) {
        requestBody.leadSourceIds = leadSourceIds;
      }
    }

    if (advancedFilters.stages && advancedFilters.stages.length > 0) {
      const stageIds = advancedFilters.stages
        .map((name) => refs.stageMap.value.get(name))
        .filter((id): id is string => id !== undefined);
      if (stageIds.length > 0) {
        requestBody.opportunityStageIds = stageIds;
      }
    }

    if (advancedFilters.opportunityTypes && advancedFilters.opportunityTypes.length > 0) {
      requestBody.dealTypeIds = advancedFilters.opportunityTypes;
    }

    if (advancedFilters.salesRep && advancedFilters.salesRep !== "all") {
      const ownerId = refs.userMap.value.get(advancedFilters.salesRep);
      if (ownerId) {
        requestBody.ownerIds = [ownerId];
      } else if (advancedFilters.salesRep) {
        requestBody.ownerIds = [advancedFilters.salesRep];
      }
    }

    if (advancedFilters.valueMin) {
      requestBody.minValue = Number(advancedFilters.valueMin);
    }
    if (advancedFilters.valueMax) {
      requestBody.maxValue = Number(advancedFilters.valueMax);
    }

    return requestBody;
  }

  function toggleAdvancedFilters() {
    advancedFiltersOpen.value = !advancedFiltersOpen.value;
  }

  function clearAdvancedFilters() {
    Object.assign(advancedFilters, getDefaultAdvancedFilters());
  }

  function setDatePreset(preset: string) {
    const now = new Date();
    let start = new Date(now);
    if (preset === "qtd") {
      const currentMonth = now.getMonth();
      const quarterStartMonth = currentMonth - (currentMonth % 3);
      start = new Date(now.getFullYear(), quarterStartMonth, 1);
    } else if (preset === "ytd") {
      start = new Date(now.getFullYear(), 0, 1);
    }
    advancedFilters.datePreset = preset;
    advancedFilters.startDate = start;
    advancedFilters.endDate = now;
  }

  function isChipSelected(
    group: keyof Pick<AdvancedFilters, "leadSources" | "stages" | "opportunityTypes">,
    value: string
  ): boolean {
    return advancedFilters[group]?.includes(value) ?? false;
  }

  function toggleChip(
    group: keyof Pick<AdvancedFilters, "leadSources" | "stages" | "opportunityTypes">,
    value: string
  ) {
    const next = new Set(advancedFilters[group]);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    advancedFilters[group] = Array.from(next);
  }

  function openPrimaryCustomPicker() {
    showPrimaryCustomPicker.value = true;
  }

  function openCompareCustomPicker() {
    showCompareCustomPicker.value = true;
  }

  function applyPrimaryCustomRange(): { error?: string } {
    if (!primaryCustomRange.from || !primaryCustomRange.to) return {};

    const from = new Date(primaryCustomRange.from);
    const to = new Date(primaryCustomRange.to);

    if (from > to) {
      return { error: "Start date must be before or equal to end date." };
    }

    advancedFilters.startDate = from;
    advancedFilters.endDate = to;
    selectedPeriod.value = "custom";
    showPrimaryCustomPicker.value = false;
    return {};
  }

  function applyCompareCustomRange(): { error?: string } {
    if (!compareCustomRange.from || !compareCustomRange.to) return {};

    const from = new Date(compareCustomRange.from);
    const to = new Date(compareCustomRange.to);

    if (from > to) {
      return { error: "Start date must be before or equal to end date." };
    }

    advancedFilters.compareStartDate = from;
    advancedFilters.compareEndDate = to;
    selectedCompareTo.value = "custom";
    showCompareCustomPicker.value = false;
    return {};
  }

  function getCurrentState() {
    return {
      selectedStatus: selectedStatus.value,
      selectedPeriod: selectedPeriod.value,
      selectedCompareTo: selectedCompareTo.value,
      advancedFilters: JSON.parse(JSON.stringify(advancedFilters)),
    };
  }

  function restoreFromView(viewData: any) {
    selectedStatus.value = viewData.selectedStatus || "all";
    selectedPeriod.value = viewData.selectedPeriod || "custom";
    selectedCompareTo.value = viewData.selectedCompareTo || "none";

    if (viewData.advancedFilters) {
      const restored = JSON.parse(JSON.stringify(viewData.advancedFilters));
      if (restored.startDate && typeof restored.startDate === "string") {
        restored.startDate = new Date(restored.startDate);
      }
      if (restored.endDate && typeof restored.endDate === "string") {
        restored.endDate = new Date(restored.endDate);
      }
      if (restored.compareStartDate && typeof restored.compareStartDate === "string") {
        restored.compareStartDate = new Date(restored.compareStartDate);
      }
      if (restored.compareEndDate && typeof restored.compareEndDate === "string") {
        restored.compareEndDate = new Date(restored.compareEndDate);
      }
      Object.assign(advancedFilters, restored);
    }
  }

  return {
    selectedStatus,
    selectedPeriod,
    selectedCompareTo,
    advancedFiltersOpen,
    advancedFilters,
    advancedFiltersCount,
    showPrimaryCustomPicker,
    showCompareCustomPicker,
    primaryCustomRange,
    compareCustomRange,
    buildRequestBody,
    toggleAdvancedFilters,
    clearAdvancedFilters,
    setDatePreset,
    isChipSelected,
    toggleChip,
    openPrimaryCustomPicker,
    openCompareCustomPicker,
    applyPrimaryCustomRange,
    applyCompareCustomRange,
    getCurrentState,
    restoreFromView,
  };
}
